import { useCallback, useEffect, useRef } from 'react';
import { getSharedRefreshPromise } from '@/libs/auth-guard';
import { tokenManager } from '@/libs/token-manager';
import type { RefreshFailureReason } from './actions/use-refresh-token-action';

/**
 * Log refresh failures with appropriate severity
 * - Temporary failures (timeout, network) use warn level - will retry automatically
 * - Permanent failures (auth) use error level - requires user action
 */
function logRefreshFailure(reason?: RefreshFailureReason) {
  switch (reason) {
    case 'timeout':
    case 'network_error':
    case 'server_error':
      // Temporary failures - will retry on next check
      console.warn(`[Token Refresh] Temporary failure: ${reason}, will retry`);
      break;
    case 'auth_failure':
      // Auth failure - user needs to re-login
      console.error('[Token Refresh] Auth failure: refresh token invalid or expired');
      break;
    case 'no_refresh_token':
      // Missing token - not really an error, just no session
      console.log('[Token Refresh] No refresh token available');
      break;
    case 'invalid_response':
      console.warn('[Token Refresh] Invalid response from server');
      break;
    default:
      console.warn('[Token Refresh] Failed with unknown reason');
  }
}

/**
 * Hook that implements proactive token refresh strategy
 *
 * Strategy:
 * 1. Proactive refresh: Check token every 30 seconds, refresh when within 2 minutes of expiry
 * 2. Visibility-based refresh: Refresh on tab focus if token is expired or expiring
 * 3. Reactive fallback: Existing 401 interceptor handles edge cases
 *
 * Best practices:
 * - Refresh at 80-90% of token lifetime (for 15min tokens = 13min elapsed)
 * - Use 2-minute buffer to prevent race conditions
 * - Stop checking when tab is hidden (battery-efficient)
 * - Clean up intervals on unmount or logout
 *
 * @param isAuthenticated - Whether the user is currently authenticated
 */
export function useTokenRefresh(isAuthenticated: boolean) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Attempts to refresh the token if it's expired or expiring soon
   * Uses a ref to prevent concurrent refresh attempts
   */
  const attemptRefresh = useCallback(async (reason: 'proactive' | 'visibility' = 'proactive') => {
    // Skip if already refreshing
    if (isRefreshingRef.current) {
      console.log('[Token Refresh] Skipping: refresh already in progress');
      return;
    }

    // Skip if no tokens exist
    const currentRefreshToken = tokenManager.getRefreshToken();
    if (!currentRefreshToken) {
      console.log('[Token Refresh] Skipping: no refresh token available');
      return;
    }

    // Check if token needs refresh (expired or within 2 minutes of expiry)
    const isExpired = tokenManager.isTokenExpired();
    const isExpiring = tokenManager.isTokenExpiring(2); // 2 minutes buffer

    if (isExpired || isExpiring) {
      try {
        isRefreshingRef.current = true;
        const timeRemaining = tokenManager.getTimeUntilExpiry();
        const minutes = Math.floor(timeRemaining / 60);
        const seconds = timeRemaining % 60;

        console.log(
          `[Token Refresh] ${reason === 'visibility' ? 'Visibility change' : 'Proactive check'}: Token ${isExpired ? 'expired' : `expiring in ${minutes}m ${seconds}s`}, refreshing...`
        );

        const result = await getSharedRefreshPromise();

        if (result.success) {
          console.log('[Token Refresh] Success: Token refreshed successfully');
        } else {
          // Use appropriate log level based on failure reason
          logRefreshFailure(result.reason);
        }
      } catch (error) {
        console.error('[Token Refresh] Error: Failed to refresh token', error);
        // Don't clear tokens here - let the 401 interceptor handle it
        // This allows for retry logic and proper error handling
      } finally {
        isRefreshingRef.current = false;
      }
    } else {
      const timeRemaining = tokenManager.getTimeUntilExpiry();
      const minutes = Math.floor(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      console.log(`[Token Refresh] Token still valid (${minutes}m ${seconds}s remaining)`);
    }
  }, []); // No dependencies - uses refs and doesn't close over any state

  /**
   * Sets up proactive token refresh and visibility change listener
   * Only runs when user is authenticated
   */
  useEffect(() => {
    // Skip if user is not authenticated
    if (!isAuthenticated) {
      console.log('[Token Refresh] Skipping: user not authenticated');
      return;
    }

    console.log('[Token Refresh] Setting up proactive refresh (30s interval, 2min buffer)');

    /**
     * Handles visibility change events
     * Refreshes token when user returns to the tab if it's expired or expiring
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Token Refresh] Tab became visible, checking token status...');
        attemptRefresh('visibility');
      }
    };

    // Initial check on mount
    attemptRefresh('proactive');

    // Set up interval for proactive refresh (every 30 seconds)
    intervalRef.current = setInterval(() => {
      // Only check if tab is visible (battery-efficient)
      if (document.visibilityState === 'visible') {
        attemptRefresh('proactive');
      }
    }, 30 * 1000); // 30 seconds

    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount or when authentication changes
    return () => {
      console.log('[Token Refresh] Cleaning up refresh intervals');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      isRefreshingRef.current = false;
    };
  }, [isAuthenticated, attemptRefresh]); // Now safe to include attemptRefresh
}
